import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, AlertTriangle, Info, Loader2, CreditCard, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { getDeviceFingerprint } from '../services/fingerprint';
import { TrialSignupModal } from '../components/TrialSignupForm';

// TuPlazaIA Logo Component
const OposaiLogo = ({ size = 48 }) => (
  <div className="flex items-center gap-3">
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="30" fill="url(#logoGradientAuth)" />
      <path d="M32 16L8 28L32 40L56 28L32 16Z" fill="#FFFFFF" />
      <path d="M32 16L8 28L16 32V42C16 42 24 48 32 48C40 48 48 42 48 42V32L56 28L32 16Z" fill="#FFFFFF" opacity="0.9"/>
      <rect x="16" y="32" width="32" height="12" rx="2" fill="#FFFFFF" opacity="0.8"/>
      <circle cx="32" cy="38" r="6" fill="url(#brainGradientAuth)" />
      <path d="M29 36.5C29 36.5 30 35 32 35C34 35 35 36.5 35 36.5" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M30 39H34" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="30" cy="37" r="1" fill="#FFFFFF"/>
      <circle cx="34" cy="37" r="1" fill="#FFFFFF"/>
      <path d="M48 28L52 32L52 44" stroke="#FFC800" strokeWidth="3" strokeLinecap="round"/>
      <circle cx="52" cy="46" r="3" fill="#FFC800"/>
      <defs>
        <linearGradient id="logoGradientAuth" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
          <stop stopColor="#58CC02"/>
          <stop offset="1" stopColor="#46A302"/>
        </linearGradient>
        <linearGradient id="brainGradientAuth" x1="26" y1="32" x2="38" y2="44" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1CB0F6"/>
          <stop offset="1" stopColor="#A560E8"/>
        </linearGradient>
      </defs>
    </svg>
    <span className="text-2xl font-extrabold tracking-tight">
      <span style={{ color: '#58CC02' }}>TuPlaza</span>
      <span style={{ color: '#1CB0F6' }}>IA</span>
    </span>
  </div>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialMode = params.get('mode') === 'register' ? 'register' : 'login';

  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [fingerprintLoading, setFingerprintLoading] = useState(true);
  const [showTrialWarning, setShowTrialWarning] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialBlocked, setTrialBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState('');

  useEffect(() => {
    initFingerprint();
  }, []);

  // Re-check trial eligibility when mode changes to register
  useEffect(() => {
    if (mode === 'register' && fingerprint && !fingerprintLoading) {
      checkTrialEligibility(fingerprint);
    }
  }, [mode, fingerprint, fingerprintLoading]);

  const initFingerprint = async () => {
    setFingerprintLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      setFingerprint(fp);
      
      // Check if device is allowed for trial (only for registration)
      if (initialMode === 'register') {
        await checkTrialEligibility(fp);
      }
    } catch (error) {
      console.error('Error loading fingerprint:', error);
    } finally {
      setFingerprintLoading(false);
    }
  };

  const checkTrialEligibility = async (fp) => {
    try {
      const response = await axios.post(`${API}/fingerprint/check`, {
        fingerprint: fp,
        action: 'trial_request'
      });
      
      console.log('Trial eligibility check:', response.data);
      
      if (!response.data.allowed) {
        setTrialBlocked(true);
        setBlockReason(response.data.reason || 'Prueba gratuita no disponible');
      } else {
        setTrialBlocked(false);
        setBlockReason('');
      }
    } catch (error) {
      console.error('Error checking trial eligibility:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For registration with email, show trial warning
    if (mode === 'register') {
      if (trialBlocked) {
        toast.error(blockReason);
        return;
      }
      setShowTrialWarning(true);
      return;
    }
    
    await performAuth();
  };

  const performAuth = async () => {
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? `${API}/auth/login` : `${API}/auth/register`;
      const fingerprintData = fingerprint ? {
        visitor_id: fingerprint.visitorId,
        components: fingerprint.components,
        confidence: fingerprint.confidence
      } : null;
      
      const data = mode === 'login' 
        ? { email, password, device_fingerprint: fingerprint?.visitorId || '' } 
        : { 
            email, 
            password, 
            name, 
            phone: phone || null, 
            birth_date: birthDate || null, 
            device_fingerprint: fingerprint?.visitorId || '',
            fingerprint_data: fingerprintData
          };

      const response = await axios.post(endpoint, data);
      localStorage.setItem('session_token', response.data.session_token);
      
      // Record trial usage and link fingerprint to user
      if (mode === 'register' && fingerprint) {
        try {
          await axios.post(`${API}/fingerprint/record-trial`, {
            fingerprint: fingerprint,
            action: 'register'
          });
        } catch (e) {
          // Non-blocking error
          console.log('Fingerprint recording failed:', e);
        }
      }
      
      toast.success(mode === 'login' ? '¡Bienvenido de vuelta!' : '¡Cuenta creada exitosamente!');
      
      // Navigate based on whether user has selected profession
      if (response.data.user.selected_profession) {
        navigate('/dashboard', { state: { user: response.data.user } });
      } else {
        navigate('/select-profession', { state: { user: response.data.user } });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error en la autenticación';
      
      // If device/IP already used trial, suggest Google
      if (error.response?.status === 403) {
        toast.error(errorMsg, {
          duration: 6000,
          action: {
            label: 'Usar Google',
            onClick: handleGoogleAuth
          }
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
      setShowTrialWarning(false);
    }
  };

  const handleGoogleAuth = async () => {
    toast.info('Redirigiendo a Google...');
    
    // Check if we're on the production domain
    const isProduction = window.location.hostname === 'tuplazaia.com' || 
                         window.location.hostname === 'www.tuplazaia.com';
    
    if (isProduction) {
      // Use standard Google OAuth on production
      window.location.href = `${API}/auth/google/login`;
    } else {
      // Use Emergent Auth for preview/development
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <OposaiLogo size={48} />
          </div>
          <h1 className="text-3xl font-bold font-heading mb-2">
            {mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
          </h1>
          <p className="text-slate-600">
            {mode === 'login'
              ? '¡Bienvenido de vuelta! Continúa tu preparación.'
              : 'Comienza tu preparación para tu oposición'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          {/* Loading Fingerprint Check */}
          {mode === 'register' && fingerprintLoading && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-sm text-blue-800">Verificando elegibilidad para prueba gratuita...</p>
            </div>
          )}

          {/* Google Auth - Recommended for Trial */}
          <div className="mb-6">
            {mode === 'register' && !trialBlocked && !fingerprintLoading && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex items-start gap-2">
                <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-emerald-800">Recomendado: Usa Google</p>
                  <p className="text-emerald-700">
                    El registro con Google garantiza tu prueba gratuita de 7 días sin restricciones.
                  </p>
                </div>
              </div>
            )}

            {/* Trial Blocked Warning */}
            {mode === 'register' && trialBlocked && !fingerprintLoading && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-800 mb-1">Prueba gratuita no disponible</p>
                    <p className="text-sm text-red-700 mb-3">{blockReason}</p>
                    <p className="text-sm text-red-600">
                      Para continuar, inicia sesión con Google o usa una cuenta existente.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Button
              onClick={handleGoogleAuth}
              variant="outline"
              className="w-full rounded-full border-2 border-slate-200 hover:border-indigo-500 py-6 text-base font-medium flex items-center justify-center gap-3"
              data-testid="google-auth-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuar con Google
            </Button>
          </div>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">O continúa con email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                    Nombre completo *
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="mt-1 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Tu nombre"
                    data-testid="name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
                      Teléfono (opcional)
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="612345678"
                      data-testid="phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="birthDate" className="text-sm font-medium text-slate-700">
                      Fecha de nacimiento
                    </Label>
                    <Input
                      id="birthDate"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className="mt-1 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      data-testid="birthdate-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="tu@email.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Contraseña
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-xl border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent pr-10"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || (mode === 'register' && trialBlocked)}
              className={`w-full rounded-full text-white py-6 text-lg font-bold shadow-lg transition-all active:scale-95 ${
                mode === 'register' && trialBlocked 
                  ? 'bg-slate-400 cursor-not-allowed' 
                  : 'bg-indigo-600 hover:bg-indigo-700 hover:shadow-indigo-500/25'
              }`}
              data-testid="auth-submit-btn"
            >
              {loading ? 'Procesando...' : mode === 'login' ? 'Iniciar Sesión' : 
                trialBlocked ? 'Usa Google para continuar' : 'Crear Cuenta'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-indigo-600 hover:text-indigo-700 font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {mode === 'login'
                ? '¿No tienes cuenta? Regístrate'
                : '¿Ya tienes cuenta? Inicia sesión'}
            </button>
          </div>
        </div>

        {/* Trial Info */}
        {mode === 'register' && (
          <div className="mt-4 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Info className="w-4 h-4" />
            <span>7 días de prueba gratuita. Sin tarjeta de crédito.</span>
          </div>
        )}
      </div>

      {/* Trial Warning Modal - Now shows card form */}
      {showTrialWarning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowTrialWarning(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <CreditCard className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Activa tu Prueba Gratuita</h3>
                <p className="text-slate-600">
                  Para comenzar tu prueba de 7 días, primero crea tu cuenta y luego añade tu tarjeta de pago.
                </p>
              </div>
            </div>
            
            <div className="bg-emerald-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-emerald-800">
                <strong>✨ Sin cargos hasta el día 7:</strong> Tu tarjeta solo se verificará. 
                El cobro de €29.99/mes solo se realizará si no cancelas antes de que termine la prueba.
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={async () => {
                  setShowTrialWarning(false);
                  await performAuth();
                  // After registration, show the trial modal
                  setShowTrialModal(true);
                }}
                className="flex-1 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
                disabled={loading}
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creando cuenta...</>
                ) : (
                  'Crear Cuenta y Continuar'
                )}
              </Button>
            </div>
            
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowTrialWarning(false)}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Cancelar
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Trial Signup Modal with Stripe */}
      <TrialSignupModal
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        onSuccess={(data) => {
          toast.success('¡Prueba gratuita activada!');
          navigate('/select-profession');
        }}
        planName="Plan Top"
        planPrice={29.99}
        trialDays={7}
      />
    </div>
  );
};

export default AuthPage;
